import React, { useState, useCallback } from 'react';

import {
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableFooter,
  TableCell,
  TableRow,
  Popover,
  TablePagination,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { FlexContainer, StyledBox, StyledGrid } from 'styles/common';

import { getColorFromKey } from 'constants/helper';
import { DotsIcon, StyledIconV2, PlusIcon2 } from 'constants/icons';
import theme from 'constants/theme';

import { deleteCustomDefinition } from 'services/customDefinition';
import { showErrorSnackbar } from 'services/snackbar';

import CircularOutlineIcon from 'uiToolkit/CircularOutlineIcon';
import SimpleAvatarGroup from 'uiToolkit/SimpleAvatarGroup';
import { Typography13, Typography14 } from 'uiToolkit/Typography';

import { CUSTOM_ROLE_ACTION_TYPES, TABLE_ROW_LIMIT, useCustomRoleContext } from './CustomRoleContext';
import NoCustomPermissionScreen from './NoCustomPermissionScreen';

const Loader = () => (
  <StyledGrid container padding="24px" direction="column" alignItems="center">
    <StyledGrid item xs="12" style={{ width: '100%' }}>
      <Skeleton variant="rect" width="100%" height="1.5rem" style={{ marginBottom: '1rem' }} />
    </StyledGrid>

    <StyledGrid container justify="space-around" style={{ width: '100%' }}>
      <StyledGrid item padding="8px" xs="6" style={{ width: '100%' }}>
        <Skeleton variant="rect" width="100%" height="1.5rem" />
      </StyledGrid>
      <StyledGrid item padding="8px" xs="6" style={{ width: '100%' }}>
        <Skeleton variant="rect" width="100%" height="1.5rem" />
      </StyledGrid>
    </StyledGrid>

    <StyledGrid container justify="space-around" style={{ width: '100%' }}>
      <StyledGrid item padding="8px" xs="6" style={{ width: '100%' }}>
        <Skeleton variant="rect" width="100%" height="1.5rem" />
      </StyledGrid>
      <StyledGrid item padding="8px" xs="6" style={{ width: '100%' }}>
        <Skeleton variant="rect" width="100%" height="1.5rem" />
      </StyledGrid>
    </StyledGrid>
  </StyledGrid>
);

const RoleMemberTable = () => {
  const {
    state: { allRoles: allData, visibleData: data, page, hasMore, isLoading, isError },
    handlePageChange,
  } = useCustomRoleContext();

  const paginationText = () => {
    const lowValue = Math.min(page * TABLE_ROW_LIMIT + 1, allData.length);
    const highValue = Math.min((page + 1) * TABLE_ROW_LIMIT, allData.length);
    return `${lowValue}-${highValue} of ${allData.length}`;
  };

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return (
      <StyledBox padding="24px">
        <Typography14 color="error" align="center">
          Failed to fetch custom permissions.
        </Typography14>
      </StyledBox>
    );
  }

  return (
    <>
      {allData.length === 0 ? (
        <NoCustomPermissionScreen />
      ) : (
        <Table>
          <TableHeader />
          <TableBody>
            {data.map(({ customRole, employees, custom }, index) => (
              <RoleNameAndMemberRow
                key={customRole?.id ?? index}
                isCustom={custom}
                customRole={customRole}
                employees={employees}
              />
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell variant="footer" padding="none" align="right" colSpan={3}>
                <TablePagination
                  component="div"
                  page={page}
                  labelDisplayedRows={paginationText}
                  rowsPerPageOptions={[]}
                  onChangePage={handlePageChange}
                  nextIconButtonProps={{ disabled: !hasMore }}
                />
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      )}
    </>
  );
};

const TableHeader = () => (
  <TableHead>
    <StyledTableCell variant="head">
      <Typography14 color={theme.palette.grey.dark}>Access Name</Typography14>
    </StyledTableCell>
    <StyledTableCell variant="head">
      <Typography14 color={theme.palette.grey.dark}>Access Type</Typography14>
    </StyledTableCell>
    <StyledTableCell variant="head">
      <Typography14 color={theme.palette.grey.dark}>Members</Typography14>
    </StyledTableCell>
  </TableHead>
);

const CustomRoleNameCell = ({ roleName }) => (
  <StyledTableCell variant="body">
    <Typography13>{roleName}</Typography13>
  </StyledTableCell>
);

const RoleMembersCell = ({
  roleMembers = [],
  isCustom,
  handleAddEditEmployees = () => {},
  handleCustomRoleDelete = () => {},
  handleEditCustomRoleAccess = () => {},
}) => (
  <StyledTableCell variant="body">
    <FlexContainer style={{ justifyContent: 'space-between' }}>
      {roleMembers.length > 0 ? (
        <SimpleAvatarGroup employees={roleMembers} maxAvatarsToShow={5} totalEmployeesCount={roleMembers.length} />
      ) : (
        <StyledBox onClick={handleAddEditEmployees} display="flex" alignItems="center" style={{ cursor: 'pointer' }}>
          <StyledIconV2 size="fixed10px" containerSize="fixed24px" circularOutline color="primary">
            <PlusIcon2 />
          </StyledIconV2>
          <Typography13 color="primary" leftGutter={1.5} style={{ display: 'inline' }}>
            Add
          </Typography13>
        </StyledBox>
      )}

      <OptionsComponent
        isCustom={isCustom}
        handleAddEditEmployees={handleAddEditEmployees}
        handleCustomRoleDelete={handleCustomRoleDelete}
        handleEditCustomRoleAccess={handleEditCustomRoleAccess}
      />
    </FlexContainer>
  </StyledTableCell>
);

const RoleNameAndMemberRow = ({ customRole, isCustom = false, employees }) => {
  const { customRoleDispatch } = useCustomRoleContext();
  const dispatch = useDispatch();

  const handleEditCustomRoleAccess = useCallback(() => {
    customRoleDispatch({
      type: CUSTOM_ROLE_ACTION_TYPES.openEditView,
      payload: {
        id: customRole.id,
      },
    });
  }, [customRole.id]);

  const handleAddEditEmployees = () => {
    customRoleDispatch({
      type: CUSTOM_ROLE_ACTION_TYPES.openEmployeeEditSidePanel,
      payload: {
        tableRowObj: {
          customRole,
          employees,
        },
      },
    });
  };

  const handleCustomRoleDelete = useCallback(() => {
    deleteCustomDefinition({ id: customRole.id })
      .then((res) => {
        customRoleDispatch({
          type: CUSTOM_ROLE_ACTION_TYPES.deleteRole,
          payload: {
            id: customRole.id,
          },
        });
      })
      .catch((err) => {
        console.error(err);
        dispatch(showErrorSnackbar(err.message || 'Failed to delete Role.'));
      });
  }, [customRole.id]);

  return (
    <StyledTableRow>
      <CustomRoleNameCell roleName={customRole.name} />
      <StyledTableCell variant="body">
        <Typography13>{isCustom ? 'Custom' : 'Admin'}</Typography13>
      </StyledTableCell>
      <RoleMembersCell
        roleMembers={employees}
        isCustom={isCustom}
        handleEditCustomRoleAccess={handleEditCustomRoleAccess}
        handleAddEditEmployees={handleAddEditEmployees}
        handleCustomRoleDelete={handleCustomRoleDelete}
      />
    </StyledTableRow>
  );
};

const OptionsComponent = ({ isCustom, handleAddEditEmployees, handleCustomRoleDelete, handleEditCustomRoleAccess }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const onEditCustomRoleAccessClick = (e) => {
    handleEditCustomRoleAccess();
    handleClose(e);
  };

  const onCustomRoleDeleteClick = (e) => {
    handleCustomRoleDelete();
    handleClose(e);
  };

  const onAddEditEmployeesClick = (e) => {
    handleAddEditEmployees();
    handleClose(e);
  };

  return (
    <>
      <StyledBox flexcontainer justifycontent="flex-end" alignitems="center" marginRight="0.5rem">
        <StyledCircularOutlineIcon onClick={handleClick} color="primary" icon={StyledDotsIcon} />
      </StyledBox>
      <Popover
        disablePortal
        id="custom-role-row"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        style={{ marginTop: '0.5rem' }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {isCustom && (
          <StyledMenuItem onClick={onEditCustomRoleAccessClick}>
            <Typography13 color={theme.palette.text.blackv1}>Edit Access</Typography13>
          </StyledMenuItem>
        )}
        <StyledMenuItem onClick={onAddEditEmployeesClick}>
          <Typography13 color={theme.palette.text.blackv1}>Edit Employees</Typography13>
        </StyledMenuItem>
        {isCustom && (
          <StyledMenuItem onClick={onCustomRoleDeleteClick}>
            <Typography13 color={theme.palette.text.blackv1}>Delete Role</Typography13>
          </StyledMenuItem>
        )}
      </Popover>
    </>
  );
};

const StyledCircularOutlineIcon = styled(CircularOutlineIcon)`
  visibility: hidden;
`;

const StyledTableRow = styled(TableRow)`
  &:hover {
    background: ${(props) => props.theme.palette.background.blue.light.v2};
  }
  &:hover ${StyledCircularOutlineIcon} {
    visibility: visible;
  }
  border-bottom: 1px solid ${getColorFromKey('lightGrey')};
`;

const StyledTableCell = styled(TableCell)`
  border-bottom: ${(props) => `1px solid ${props.theme.palette.borders.lighterGrey}`};
`;

const StyledDotsIcon = styled(DotsIcon)`
  width: 13px;
  margin-top: 1px;
`;

const StyledMenuItem = styled(MenuItem)`
  padding-right: 30px;
`;

export default RoleMemberTable;
